/*
 * Diagnostic phase — 4 sections (port of HTML screen-0..3).
 *
 *   §1 Operations   — 5 Likert (1..5) questions: op1..op5
 *   §2 Experience   — 6 IHI (1..4) choice questions: exp1..exp6
 *   §3 Readiness    — gap1 (1..5 Likert), gap2 + gap3 (enums)
 *   §4 Morning After — three optional miracle textareas
 *
 * Question text and option labels are driven from PlanBuilderConfig
 * so the admin panel can configure them. Copy defaults match the
 * prototype verbatim.
 */
import { useMemo } from 'react';
import type { SharedLens } from '@shared/lens';
import type {
  DiagnosticAnswers,
  LikertExp,
  LikertOp,
  PrimaryBlocker,
  PremortemReason,
} from '../types';
import { usePlanBuilderConfigContext } from '../PlanBuilderConfigContext';

export type DiagnosticSection = 0 | 1 | 2 | 3;

interface Props {
  section: DiagnosticSection;
  answers: DiagnosticAnswers;
  lens: SharedLens;
  onAnswerChange(next: DiagnosticAnswers): void;
  onBack(): void;
  onNext(): void;
}

export function DiagnosticPhase({
  section,
  answers,
  lens,
  onAnswerChange,
  onBack,
  onNext,
}: Props) {
  const config = usePlanBuilderConfigContext();
  const { diagnostic } = config;

  // Build the typed question arrays from config at render time.
  const opQuestions = useMemo(
    () =>
      ([1, 2, 3, 4, 5] as const).map((n) => ({
        id: `op${n}` as 'op1' | 'op2' | 'op3' | 'op4' | 'op5',
        q: diagnostic.op[n].text,
      })),
    [diagnostic],
  );

  const expQuestions = useMemo(
    () =>
      ([1, 2, 3, 4, 5, 6] as const).map((n) => ({
        id: `exp${n}` as 'exp1' | 'exp2' | 'exp3' | 'exp4' | 'exp5' | 'exp6',
        prompt: diagnostic.exp[n].text,
        options: (diagnostic.exp[n].options ?? []).map((label, idx) => ({
          val: (idx + 1) as 1 | 2 | 3 | 4,
          label,
        })),
      })),
    [diagnostic],
  );

  const blockerOptions = useMemo(
    () =>
      (
        ['workload', 'process', 'visibility', 'tools', 'culture', 'clarity'] as PrimaryBlocker[]
      ).map((val) => ({ val, label: diagnostic.gap2.options[val] })),
    [diagnostic],
  );

  const premortemOptions = useMemo(
    () =>
      (
        [
          'leadership',
          'capacity',
          'buyin',
          'measurement',
          'scope',
          'external',
        ] as PremortemReason[]
      ).map((val) => ({ val, label: diagnostic.gap3.options[val] })),
    [diagnostic],
  );

  // For each section, count answered questions vs required total. The
  // "Continue" button stays disabled until all required fields land.
  const sectionInfo = useMemo(() => {
    if (section === 0) {
      const done = opQuestions.filter((q) => answers[q.id] !== undefined).length;
      return { done, total: opQuestions.length, label: `${done} of ${opQuestions.length} answered` };
    }
    if (section === 1) {
      const done = expQuestions.filter((q) => answers[q.id] !== undefined).length;
      return { done, total: expQuestions.length, label: `${done} of ${expQuestions.length} answered` };
    }
    if (section === 2) {
      const fields: Array<keyof DiagnosticAnswers> = ['gap1', 'gap2', 'gap3'];
      const done = fields.filter((f) => answers[f] !== undefined).length;
      return { done, total: 3, label: `${done} of 3 answered` };
    }
    return { done: 0, total: 0, label: "All three are optional. Skip what doesn't land." };
  }, [section, answers, opQuestions, expQuestions]);

  const valid = section === 3 ? true : sectionInfo.done >= sectionInfo.total;

  return (
    <div className="pb-section-wrap">
      {section === 0 && (
        <Section1Operations
          answers={answers}
          onAnswerChange={onAnswerChange}
          questions={opQuestions}
        />
      )}
      {section === 1 && (
        <Section2Experience
          answers={answers}
          lens={lens}
          onAnswerChange={onAnswerChange}
          questions={expQuestions}
        />
      )}
      {section === 2 && (
        <Section3Readiness
          answers={answers}
          onAnswerChange={onAnswerChange}
          gap1Text={diagnostic.gap1.text}
          gap2Text={diagnostic.gap2.text}
          gap3Text={diagnostic.gap3.text}
          blockerOptions={blockerOptions}
          premortemOptions={premortemOptions}
        />
      )}
      {section === 3 && (
        <Section4Miracle
          answers={answers}
          onAnswerChange={onAnswerChange}
          miracleCopy={diagnostic.miracle}
        />
      )}

      <div className="pb-section-nav">
        <button type="button" className="pb-btn-back" onClick={onBack}>← Back</button>
        <span className="pb-section-counter">{sectionInfo.label}</span>
        <button
          type="button"
          className="pb-btn-next"
          onClick={onNext}
          disabled={!valid}
        >
          {section === 3 ? 'Build my plan →' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}

// ─── Sub-section components ────────────────────────────────────────
function Section1Operations({
  answers,
  onAnswerChange,
  questions,
}: {
  answers: DiagnosticAnswers;
  onAnswerChange(a: DiagnosticAnswers): void;
  questions: ReadonlyArray<{ id: 'op1' | 'op2' | 'op3' | 'op4' | 'op5'; q: string }>;
}) {
  return (
    <>
      <div className="pb-section-eyebrow">Section 1 of 4 · Operations</div>
      <h2 className="pb-section-title">How work arrives and moves</h2>
      <p className="pb-section-sub">
        How accurately does each statement describe your team? (1 = strongly
        disagree, 5 = strongly agree)
      </p>
      {questions.map((q) => (
        <div key={q.id} className="pb-qcard">
          <p className="pb-qtext">{q.q}</p>
          <ScaleRow1to5
            value={answers[q.id]}
            onChange={(v) => onAnswerChange({ ...answers, [q.id]: v })}
            ariaLabel={q.q}
          />
          <div className="pb-scale-labels">
            <span>Strongly disagree</span>
            <span>Strongly agree</span>
          </div>
        </div>
      ))}
    </>
  );
}

function Section2Experience({
  answers,
  lens,
  onAnswerChange,
  questions,
}: {
  answers: DiagnosticAnswers;
  lens: SharedLens;
  onAnswerChange(a: DiagnosticAnswers): void;
  questions: ReadonlyArray<{
    id: 'exp1' | 'exp2' | 'exp3' | 'exp4' | 'exp5' | 'exp6';
    prompt: string;
    options: ReadonlyArray<{ val: 1 | 2 | 3 | 4; label: string }>;
  }>;
}) {
  const feelText = lens.emotional.feel.length ? lens.emotional.feel.join(', ') : null;
  const avoidText = lens.emotional.avoid.length ? lens.emotional.avoid.join(', ') : null;
  return (
    <>
      <div className="pb-section-eyebrow">Section 2 of 4 · Experience</div>
      <h2 className="pb-section-title">How the team experiences the work</h2>
      <p className="pb-section-sub">
        Choose the statement that best reflects your team's reality right now.
      </p>
      {(feelText || avoidText) && (
        <div className="pb-lens-prefilled-banner" style={{ marginTop: 4 }}>
          <strong>From your lens:</strong>{' '}
          {feelText && (
            <>
              you said the team should feel <strong>{feelText}</strong>.{' '}
            </>
          )}
          {avoidText && (
            <>
              You want them to stop <strong>{avoidText}</strong>.{' '}
            </>
          )}
          The questions below check how today's reality lines up with that —
          answer based on what's actually happening now.
        </div>
      )}
      {questions.map((q) => (
        <div key={q.id} className="pb-qcard">
          <p className="pb-qtext">{q.prompt}</p>
          <div className="pb-options" role="radiogroup" aria-label={q.prompt}>
            {q.options.map((opt) => {
              const on = answers[q.id] === opt.val;
              return (
                <button
                  key={opt.val}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={`pb-option${on ? ' selected' : ''}`}
                  onClick={() =>
                    onAnswerChange({ ...answers, [q.id]: opt.val as LikertExp })
                  }
                >
                  <span className="pb-option-dot" />
                  <span className="pb-option-text">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}

function Section3Readiness({
  answers,
  onAnswerChange,
  gap1Text,
  gap2Text,
  gap3Text,
  blockerOptions,
  premortemOptions,
}: {
  answers: DiagnosticAnswers;
  onAnswerChange(a: DiagnosticAnswers): void;
  gap1Text: string;
  gap2Text: string;
  gap3Text: string;
  blockerOptions: ReadonlyArray<{ val: PrimaryBlocker; label: string }>;
  premortemOptions: ReadonlyArray<{ val: PremortemReason; label: string }>;
}) {
  return (
    <>
      <div className="pb-section-eyebrow">Section 3 of 4 · Readiness</div>
      <h2 className="pb-section-title">Where change meets reality</h2>
      <p className="pb-section-sub">
        Three questions that shape how we pressure-test your plan.
      </p>
      <div className="pb-qcard">
        <p className="pb-qtext">
          {gap1Text}
        </p>
        <ScaleRow1to5
          value={answers.gap1}
          onChange={(v) => onAnswerChange({ ...answers, gap1: v })}
          ariaLabel={gap1Text}
        />
        <div className="pb-scale-labels">
          <span>Strongly disagree</span>
          <span>Strongly agree</span>
        </div>
      </div>
      <div className="pb-qcard">
        <p className="pb-qtext">
          {gap2Text}
        </p>
        <div className="pb-options" role="radiogroup" aria-label="Primary blocker">
          {blockerOptions.map((opt) => {
            const on = answers.gap2 === opt.val;
            return (
              <button
                key={opt.val}
                type="button"
                role="radio"
                aria-checked={on}
                className={`pb-option${on ? ' selected' : ''}`}
                onClick={() => onAnswerChange({ ...answers, gap2: opt.val })}
              >
                <span className="pb-option-dot" />
                <span className="pb-option-text">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="pb-qcard">
        <p className="pb-qtext">
          {gap3Text}
        </p>
        <div className="pb-options" role="radiogroup" aria-label="Pre-mortem reason">
          {premortemOptions.map((opt) => {
            const on = answers.gap3 === opt.val;
            return (
              <button
                key={opt.val}
                type="button"
                role="radio"
                aria-checked={on}
                className={`pb-option${on ? ' selected' : ''}`}
                onClick={() => onAnswerChange({ ...answers, gap3: opt.val })}
              >
                <span className="pb-option-dot" />
                <span className="pb-option-text">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

function Section4Miracle({
  answers,
  onAnswerChange,
  miracleCopy,
}: {
  answers: DiagnosticAnswers;
  onAnswerChange(a: DiagnosticAnswers): void;
  miracleCopy: { desk: string; midday: string; gc: string };
}) {
  const update = (k: 'miracle_desk' | 'miracle_midday' | 'miracle_gc', v: string) => {
    const trimmed = v.trim();
    const next = { ...answers };
    if (trimmed) next[k] = v;
    else delete next[k];
    onAnswerChange(next);
  };
  return (
    <>
      <div className="pb-section-eyebrow">Section 4 of 4 · Reset</div>
      <h2 className="pb-section-title">The Morning After</h2>
      <p className="pb-section-sub">
        Two minutes. No wrong answers. Possibly the most useful thing on this
        entire diagnostic — if you give it a real attempt.
      </p>
      <div className="pb-miracle-frame">
        <p>
          <strong>
            Imagine that in the middle of the night, as you're sleeping, a
            miracle happens.
          </strong>{' '}
          The thing you're most stressed about with your legal team has been
          solved. Gone. But because the miracle happened while you slept, you
          don't know it's happened — you wake up to a normal Monday morning.
        </p>
        <p>
          <strong>
            What are the first things you'd notice as the day starts that would
            tell you something had changed?
          </strong>
        </p>
      </div>
      <div className="pb-qcard">
        <p className="pb-qtext">{miracleCopy.desk}</p>
        <textarea
          className="pb-miracle-input"
          rows={3}
          value={answers.miracle_desk ?? ''}
          onChange={(e) => update('miracle_desk', e.target.value)}
          placeholder="e.g., the inbox isn't on fire. There's a clear list of what matters today. Nothing waiting on me from yesterday."
        />
      </div>
      <div className="pb-qcard">
        <p className="pb-qtext">{miracleCopy.midday}</p>
        <textarea
          className="pb-miracle-input"
          rows={3}
          value={answers.miracle_midday ?? ''}
          onChange={(e) => update('miracle_midday', e.target.value)}
          placeholder="e.g., people are heads-down on real work, not chasing status updates. Someone's running an experiment instead of firefighting."
        />
      </div>
      <div className="pb-qcard">
        <p className="pb-qtext">
          {miracleCopy.gc}
        </p>
        <textarea
          className="pb-miracle-input"
          rows={3}
          value={answers.miracle_gc ?? ''}
          onChange={(e) => update('miracle_gc', e.target.value)}
          placeholder="e.g., they'd see contracts moving without escalation. They'd notice that legal isn't the bottleneck on the deal anymore."
        />
      </div>
      <p className="pb-miracle-footnote">
        We use these to make your plan more specific — and to learn, in
        aggregate and anonymously, what legal teams are actually trying to
        become. Nothing identifying you appears anywhere unless you choose to
        share it on the next screen.
      </p>
    </>
  );
}

// ─── 5-step Likert pill row, used by op1..op5 + gap1 ────────────────
function ScaleRow1to5({
  value,
  onChange,
  ariaLabel,
}: {
  value: number | undefined;
  onChange(v: LikertOp): void;
  ariaLabel: string;
}) {
  return (
    <div className="pb-scale-row" role="radiogroup" aria-label={ariaLabel}>
      {[1, 2, 3, 4, 5].map((v) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={value === v}
          className={`pb-sbtn${value === v ? ' sel' : ''}`}
          onClick={() => onChange(v as LikertOp)}
        >
          {v}
        </button>
      ))}
    </div>
  );
}
